import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, LogIn, ShieldCheck, School, BookOpen, Wallet, Users, Briefcase, Crown, Bus } from "lucide-react";

const ROLE_ICONS = {
  master_admin: Crown,
  super_admin: ShieldCheck,
  principal: School,
  teacher: BookOpen,
  accountant: Wallet,
  parent: Users,
  hr_manager: Briefcase,
  transport_staff: Bus,
};

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [demos, setDemos] = useState([]);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/meta/demo-accounts").then((r) => setDemos(r.data)).catch(() => {});
  }, []);

  async function submit(e) {
    e?.preventDefault?.();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to SPARKORA EDU");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  async function demoLogin(d) {
    setEmail(d.email);
    setPassword(d.password);
    setError("");
    setLoading(true);
    try {
      await login(d.email, d.password);
      toast.success(`Signed in as ${d.label}`);
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen auth-gradient flex flex-col">
      <div className="px-6 lg:px-10 py-6">
        <Logo size="md" />
      </div>

      <div className="flex-1 grid lg:grid-cols-2 gap-10 lg:gap-16 px-6 lg:px-16 pb-10 items-center max-w-7xl mx-auto w-full">
        {/* Hero */}
        <div className="hidden lg:block">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur border border-violet-200 text-xs font-semibold text-violet-700 uppercase tracking-wider mb-6">
            <Sparkle />
            Role-based school platform
          </div>
          <h1 className="font-display text-5xl xl:text-6xl font-extrabold text-slate-900 leading-[1.05] tracking-tight">
            School operations,<br />
            <span className="text-violet-600">unified across every branch.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-lg leading-relaxed">
            One premium platform for Playschool, Elementary, Primary and Secondary — manage students, attendance,
            staff and branches with role-based clarity.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-5 max-w-lg">
            <Stat label="Roles" value="7" />
            <Stat label="School types" value="4" />
            <Stat label="Attendance states" value="4" />
          </div>
        </div>

        {/* Login card */}
        <Card className="shadow-2xl shadow-violet-900/10 border-violet-100 bg-white/90 backdrop-blur">
          <CardContent className="p-7 sm:p-10">
            <div className="mb-7">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Sign in</h2>
              <p className="mt-1 text-sm text-slate-500">Use a demo card for 1-click access, or enter credentials manually.</p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium">Email or mobile number</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="text"
                  value={email}
                  autoComplete="username"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@sparkora.edu or 98765 43210"
                  required
                  className="mt-1.5 input-focus"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-1.5 input-focus"
                />
              </div>
              {error && (
                <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}
              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <><LogIn size={18} className="mr-2" /> Sign in</>}
              </Button>
            </form>

            {demos.length > 0 && <div className="my-6 flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Demo accounts</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>}

            <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
              {demos.map((d) => {
                const Icon = ROLE_ICONS[d.role] || ShieldCheck;
                return (
                  <button
                    key={d.role}
                    data-testid={`demo-login-${d.role.replace(/_/g, "-")}-btn`}
                    disabled={loading}
                    onClick={() => demoLogin(d)}
                    className="group text-left px-3 py-2.5 rounded-lg border border-violet-100 bg-violet-50/40 hover:bg-violet-600 hover:border-violet-600 transition-all"
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={15} className="text-violet-600 group-hover:text-white shrink-0" />
                      <span className="text-sm font-semibold text-slate-800 group-hover:text-white truncate">
                        {d.label}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 group-hover:text-violet-100 mt-0.5 truncate">
                      {d.email}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-3xl font-extrabold text-slate-900">{value}</div>
      <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function Sparkle() {
  return <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />;
}
